import React from 'react'
import ReactDOM from 'react-dom/client'
import posthog from 'posthog-js'
import * as Sentry from '@sentry/react'
import App from './App.jsx'
import './index.css'
// Design-system tokens, scoped to `.ds` so they cannot restyle existing screens.
// See src/styles/design-tokens.css for why they are not published at :root.
import './styles/design-tokens.css'
// Explorer wall palette, scoped to `.explorer-wall`.
import './styles/explorer-tokens.css'
import { versionedUrl, hasVersionMarker } from './utils/versionReload';
import { initMetaPixel } from './utils/metaPixel';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0'),
    replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),
  })
}

posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
  // Stated rather than left to the library's internal default. This read an env var that was
  // never set — Vercel has VITE_POSTHOG_URL, a different name — so it silently resolved to
  // posthog-js's own fallback, and it took reading the deployed bundle to work out where
  // events were actually going.
  api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
  autocapture: false,
  // This is a single-page app, so the browser only loads a document once. On the library's
  // legacy default that means exactly one $pageview per session — the landing page — and every
  // route change after it is invisible. Set explicitly rather than via `defaults`, which would
  // also move unrelated behaviours to their newer values.
  capture_pageview: 'history_change',
})
/**
 * Which product surface an event came from.
 *
 * The iOS app reuses ten of this app's event names on purpose — `wall_viewed`, `tile_opened`,
 * `hold_taken` and the rest mean the same thing wherever they happen, and splitting them into
 * `wall_viewed_ios` would make every cross-platform question a manual union.
 *
 * The cost of that is this line. Without it the two are indistinguishable, and every funnel
 * built here in August would quietly start counting iPhone traffic it was not designed to
 * include. Registered on both sides rather than treating web as "not ios", because an absent
 * value is a definition nobody can find later. See mobile/src/services/analytics.ts.
 */
posthog.register({ platform: 'web' })

/**
 * Meta pixel. Loaded here so it is running before the first route renders — App.jsx sends
 * the pageview, and the pixel has to exist by then to read fbclid off the entry URL.
 * A no-op unless VITE_META_PIXEL_ID is set; see utils/metaPixel.js.
 */
initMetaPixel()
/**
 * Recover users whose cached index.html references chunks that no longer exist after a
 * redeploy. Without this, the page stays blank because React never mounts.
 *
 * Three things had to change, because as written this fired on every single load of
 * /dashboard with every chunk on the server returning 200 — a guaranteed double page load
 * on the app's main landing route, for a staleness that did not exist.
 *
 * 1. It reloads at most ONCE. `recovering` only guarded within a single page load, so a
 *    reload cleared the guard and the next load was free to do it again. The marker is the
 *    ?v= parameter the reload itself adds: if it is already there, the reload has been
 *    tried and did not help, so trying it again just burns another round trip.
 *
 * 2. It no longer clears sessionStorage. That was the damaging part, not the reload.
 *    Firebase Auth parks its pending-redirect state in sessionStorage under
 *    firebase:pendingRedirect:*, and signInWithRedirect in AuthContext stores
 *    authRedirectInFlight there too. Wiping it mid-sign-in leaves getRedirectResult with
 *    nothing to resolve. A Google sign-in lands on /dashboard, which is precisely where
 *    this was firing, so the flow was destroying the state it was about to need. The
 *    session itself survives in localStorage, which is why sign-in eventually completed
 *    anyway and the damage looked like slowness rather than failure.
 *
 * 3. It records what it saw. The reload wipes the console, which made the triggering error
 *    unobservable from outside — the reason this went unnoticed. The message and stack now
 *    go to localStorage first, so the next occurrence can be read after the fact.
 */
const STALE_CHUNK_RE = /Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|ChunkLoadError|error loading dynamically imported module/i;
let recovering = false;
const recoverFromStaleChunk = (msg, err) => {
  if (recovering || !STALE_CHUNK_RE.test(msg || '')) return;

  // Whether or not we reload, leave a record. This is the only trace that survives.
  try {
    localStorage.setItem('lastChunkError', JSON.stringify({
      message: String(msg).slice(0, 300),
      stack: String(err?.stack || '').slice(0, 800),
      path: window.location.pathname,
      at: new Date().toISOString(),
      reloaded: !hasVersionMarker(),
    }));
  } catch {}

  // Already came back from a recovery reload and still broken: reloading again would only
  // cost another round trip. Let the error surface instead of hiding it in a loop.
  if (hasVersionMarker()) {
    console.error('[chunk-recovery] still failing after reload, not retrying:', msg);
    return;
  }

  recovering = true;
  // versionedUrl, not `pathname + '?v='` — the old form dropped ?ref=, utm tags and invite
  // tokens on the way to the recovered page.
  window.location.replace(versionedUrl());
};
window.addEventListener('error', (e) => recoverFromStaleChunk(e?.message, e?.error));
window.addEventListener('unhandledrejection', (e) => recoverFromStaleChunk(e?.reason?.message, e?.reason));

ReactDOM.createRoot(document.getElementById('root')).render(
  <App />
)

